import React, { useCallback, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useSelector, useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import { Button, ButtonType } from "components/common/Button.component";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import * as reduxSelectors from "components/store/application.reducers";
import { approveMultipleTasks } from "components/mytasks/action/approveMultipleTasks.action.js";
import { ReApproveProps } from "types/modals";
import { Store } from "redux";

/**
 * shown when the user edited a document whose workflow asks for confirmation before restarting:
 * they can send it back round to the previous approvers, or approve it without re-approval
 */
const ReApprove = ({ more, closeModal, loopFocus, initialFocusRef }: ReApproveProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const userSettings = useSelector((store: Store) => reduxSelectors.getUsersSettings(store));
    const actionParameters = useSelector((store: Store) => reduxSelectors.getActionParameters(store));

    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);

    const showComments = userSettings?.showCommentOnApprove;

    const approve = useCallback(
        (skipWorkflowRestart: boolean) => {
            setActionRunning(true);

            dispatch(
                approveMultipleTasks(
                    actionParameters?.tasksData,
                    comment || null,
                    more?.taskDetails?.documentVersion,
                    skipWorkflowRestart,
                    false,
                    more?.navigate,
                ),
            );
            closeModal();
        },
        [actionParameters, comment, more?.taskDetails, more?.navigate, dispatch, closeModal],
    );

    const enabled = !actionRunning;

    return (
        <>
            <div className="ga-modal__content">
                <div>{translate("popUp.reapprove.info")}</div>
                {showComments && (
                    <Comment
                        ref={initialFocusRef}
                        inputHint={translate("popUp.approve.commentHint")}
                        translate={translate}
                        shouldFocusFirst={true}
                        tabIndex={MODAL_CONTENT_TAB_INDEX}
                        id="comment"
                        value={comment}
                        propagateValue={setComment}
                        label={translate("popUp.approve.commentLabel")}
                    />
                )}
            </div>
            <div className="ga-modal__actions">
                <Button
                    ref={showComments ? undefined : initialFocusRef}
                    type={ButtonType.PRIMARY}
                    text={translate("popUp.reapprove.reApprove")}
                    onClick={() => approve(false)}
                    disabled={!enabled}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                    extraClassName="btn-margin-right ast_ok_btn"
                />
                <Button
                    type={ButtonType.SECONDARY}
                    text={translate("popUp.reapprove.notReApprove")}
                    onClick={() => approve(true)}
                    disabled={!enabled}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                    extraClassName="btn-margin-right"
                />
                <Button
                    type={ButtonType.SECONDARY}
                    text={translate("popUp.cancel")}
                    onClick={closeModal}
                    onBlur={loopFocus}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 3}
                    extraClassName="ast_cancel_btn"
                />
            </div>
        </>
    );
};

export default ReApprove;
